import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useFlight } from '../contexts/FlightContext';
import { useAuth } from '../contexts/AuthContext';
import { ScannedPassenger } from '../types';

const SecurityScreen: React.FC = () => {
  const { selectedFlightData: flightData, addScannedPassenger, showNotification } = useFlight();
  const { securityScanPercentage } = useAuth();
  
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    seatNumber: string;
    name: string;
    note: string;
  }>({
    isOpen: false,
    seatNumber: '',
    name: '',
    note: '',
  });

  if (!flightData) return null;
  
  const { totalPassengers, totalPassengersSource } = useMemo(() => {
    const finalTotalStr = flightData.counterDistribution.finalFigures.totalPassengers;
    const finalTotal = parseInt(finalTotalStr, 10);
    if (!isNaN(finalTotal) && finalTotal > 0) {
      return { totalPassengers: finalTotal, totalPassengersSource: 'Final Figures' };
    }

    const bookingTotalStr = flightData.counterDistribution.booking.totalPassengers;
    const bookingTotal = parseInt(bookingTotalStr, 10);
    const total = isNaN(bookingTotal) ? 0 : bookingTotal;
    return { totalPassengers: total, totalPassengersSource: 'Booking Figures' };
  }, [flightData.counterDistribution.finalFigures, flightData.counterDistribution.booking]);


  const targetScans = useMemo(() => {
    if (totalPassengers === 0) return 0;
    return Math.ceil(totalPassengers * (securityScanPercentage / 100));
  }, [totalPassengers, securityScanPercentage]);

  const scannedPassengers = flightData.scannedPassengers || [];

   const handleOpenManualEntry = useCallback(() => {
    setModalState({
        isOpen: true,
        seatNumber: '',
        name: '',
        note: 'Manually entered',
    });
  }, []);

  const handleModalChange = useCallback((field: 'seatNumber' | 'name' | 'note', value: string) => {
    let processedValue = value;
    if (field === 'seatNumber' || field === 'name') {
        processedValue = value.toUpperCase();
    }
    setModalState(prev => ({ ...prev, [field]: processedValue }));
  }, []);

  const savePassenger = useCallback(() => {
    const { seatNumber, name, note } = modalState;
    if (!seatNumber.trim()) {
        showNotification("Seat number is required.", 'error');
        return false;
    }
    const finalSeatNumber = seatNumber.trim();
    if (scannedPassengers.some(p => p.seatNumber === finalSeatNumber)) {
        showNotification(`Seat ${finalSeatNumber} has already been scanned.`, 'error');
        return false;
    }
    const passengerData: Omit<ScannedPassenger, 'sequence' | 'scannedAt' | 'scannedBy'> = {
        name: name.trim() || 'N/A',
        seatNumber: finalSeatNumber,
        note: note.trim(),
    };
    addScannedPassenger(passengerData);
    showNotification('Passenger added successfully.', 'success');
    return true;
  }, [modalState, addScannedPassenger, showNotification, scannedPassengers]);

  const handleConfirmPassenger = useCallback(() => {
    if (savePassenger()) {
        setModalState({ isOpen: false, seatNumber: '', name: '', note: '' });
    }
  }, [savePassenger]);

  const handleCancelPassenger = useCallback(() => {
    setModalState({ isOpen: false, seatNumber: '', name: '', note: '' });
  }, []);
  
  const renderPassengerDetailsModal = () => {
    if (!modalState.isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md animate-slide-up-fade-in">
                <h2 className="text-2xl font-bold text-white mb-4">
                    Confirm Passenger Details
                </h2>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="modalSeatNumber" className="block text-sm font-medium text-slate-300 mb-1">Seat Number*</label>
                        <input
                            id="modalSeatNumber"
                            type="text"
                            value={modalState.seatNumber}
                            onChange={(e) => handleModalChange('seatNumber', e.target.value)}
                            className="w-full uppercase"
                            placeholder="E.G., 12D"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="modalNameEntry" className="block text-sm font-medium text-slate-300 mb-1">Passenger Name</label>
                        <input
                            id="modalNameEntry"
                            type="text"
                            value={modalState.name}
                            onChange={(e) => handleModalChange('name', e.target.value)}
                            className="w-full uppercase"
                            placeholder="PASSENGER NAME"
                        />
                    </div>
                    <div>
                        <label htmlFor="modalNote" className="block text-sm font-medium text-slate-300 mb-1">Add Note (Optional)</label>
                        <textarea
                            id="modalNote"
                            value={modalState.note}
                            onChange={(e) => handleModalChange('note', e.target.value)}
                            rows={2}
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-slate-700">
                    <button onClick={handleCancelPassenger} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded-lg">Cancel</button>
                    <button onClick={handleConfirmPassenger} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg flex items-center">Confirm</button>
                </div>
            </div>
        </div>
    );
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <h2 className="text-xl font-bold text-blue-400 mb-2">ETD Sheet Security Scan</h2>
        <div className="flex justify-between items-center text-lg">
           <p className="text-slate-300">
             Target Scans: <span className="font-bold text-amber-400">{targetScans}</span> 
             <span className="text-sm text-slate-400"> ({securityScanPercentage}% of {totalPassengers} from {totalPassengersSource})</span>
           </p>
           <p className="text-slate-300">
             Completed: <span className="font-bold text-green-400">{scannedPassengers.length}</span>
           </p>
        </div>
         <div className="w-full bg-slate-700 rounded-full h-2.5 mt-2">
            <div className="bg-green-500 h-2.5 rounded-full" style={{ width: `${targetScans > 0 ? (scannedPassengers.length / targetScans) * 100 : 0}%` }}></div>
        </div>
      </div>
      
      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <div data-tour-id="security-scan-button" className="flex justify-center flex-wrap gap-4">
            <button
                onClick={handleOpenManualEntry}
                className="px-6 py-3 rounded-lg text-white font-bold flex items-center gap-3 transition-all duration-200 transform hover:scale-105 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 shadow-lg"
            >
                Add Manually
            </button>
        </div>
      </div>

      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
         <h3 className="text-lg font-bold text-blue-400 mb-4">Scanned Passengers</h3>
         <div className="max-h-96 overflow-y-auto">
             {scannedPassengers.length > 0 ? (
                <table className="min-w-full divide-y divide-slate-700">
                    <thead className="bg-slate-700/50">
                        <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-300">Seq.</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-300">Name</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-300">Seat</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-300">Note</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-300">Time / By</th>
                        </tr>
                    </thead>
                    <tbody className="bg-slate-800 divide-y divide-slate-700">
                        {scannedPassengers.slice().reverse().map(p => (
                            <tr key={p.sequence}>
                                <td className="px-4 py-3 text-sm text-white">{p.sequence}</td>
                                <td className="px-4 py-3 text-sm text-white">{p.name}</td>
                                <td className="px-4 py-3 text-sm text-white font-mono">{p.seatNumber}</td>
                                <td className="px-4 py-3 text-sm text-slate-300 italic">{p.note || '-'}</td>
                                <td className="px-4 py-3 text-xs text-slate-400">
                                    {new Date(p.scannedAt).toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'})} by {p.scannedBy}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
             ) : (
                <p className="text-center text-slate-400 py-4">No passengers scanned yet.</p>
             )}
         </div>
      </div>
      
      {renderPassengerDetailsModal()}
    </div>
  );
};

export default SecurityScreen;